import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { updateSentryForMFE } from '../utils/sentry';
import { detectLocale } from '../utils/localeUtils';

const HAWKEYE_SDK_FILE_NAME = 'hawkeye.min.js';

const getShellAssetUrl = (fileName: string): string => {
  const publicPath =
    typeof __webpack_public_path__ === 'string' && __webpack_public_path__
      ? __webpack_public_path__
      : '/';
  return `${publicPath}${fileName}`;
};

export interface UseModFedResult {
  /** True while the dynamic import and remote `default()` mount are in progress. */
  isRemoteLoading: boolean;
}

export const useModFed = (
  rootID: string,
  importRemote,
): UseModFedResult => {
  const history = useHistory();
  const localeCountryCode = detectLocale();
  console.log('🌍 shell app: Locale country code detected:', localeCountryCode);
  const [isRemoteLoading, setIsRemoteLoading] = useState(
    () => importRemote != null,
  );

  const [config] = useState({
    defaultHistory: history,
    onNavigate: ({ pathname: nextPath }) => {
      const { pathname } = history.location;
      if (pathname !== nextPath) {
        history.push(nextPath);
      }
    },
    updateSentryForMFE,
    localeCountryCode,
    hawkeyeSdkUrl: getShellAssetUrl(HAWKEYE_SDK_FILE_NAME),
  });

  useEffect(() => {
    let unmountFunc: (() => void) | undefined;
    if (!importRemote) {
      return;
    }
    const mountAMS = async () => {
      try {
        const module = await importRemote();
        const { onParentNavigate, unmount } = module.default(
          document.getElementById(rootID),
          config,
        );
        unmountFunc = unmount;
        if (config.defaultHistory && onParentNavigate) {
          config.defaultHistory.listen(onParentNavigate);
        }
      } catch (error) {
        console.log('Failed to mount remote module:', error);
      } finally {
        setIsRemoteLoading(false);
      }
    };
    mountAMS();

    return () => {
      if (unmountFunc) {
        unmountFunc();
      }
    };
  }, []);

  return { isRemoteLoading };
};